import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
} from '@mui/material';
import { 
  Logout, 
  AccountCircle, 
  QueryStats as QueryStatsIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import AdminNavigationDrawer from './AdminNavigationDrawer';
import { useLocation } from 'react-router-dom';

const Layout = ({ children }) => {
  const { user, logout, hasModuleAccess } = useAuth();
  const { showSuccess } = useNotification();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'));

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    showSuccess('Logout realizado com sucesso!');
    handleMenuClose();
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Verificar se está em uma página administrativa
  const isAdminPage = location.pathname.startsWith('/admin');

  // Verificar se o usuário tem acesso a funcionalidades administrativas
  const canAccessAdmin = hasModuleAccess && (
    hasModuleAccess('users') || 
    hasModuleAccess('finance') || 
    hasModuleAccess('inventory') || 
    hasModuleAccess('rbac') ||
    hasModuleAccess('analytics')
  );

  const getUserInitials = (name) => {
    if (!name) return '?';
    const nameParts = name.split(' ');
    const firstInitial = nameParts[0] ? nameParts[0][0] : '';
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Drawer de navegação administrativa */}
      {canAccessAdmin && isAdminPage && (
        <AdminNavigationDrawer 
          open={drawerOpen} 
          onToggle={handleDrawerToggle}
          mobile={isSmall}
        />
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <AppBar
          position="sticky"
          color="default"
          elevation={1}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
        <Toolbar variant={ isSmall ? 'dense' : 'regular' }>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <QueryStatsIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {process.env.REACT_APP_TITLE || 'BI Dashboard'}
            </Typography>
          </Box>

          <IconButton onClick={handleMenuOpen} size="small">
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
              {getUserInitials(user?.full_name)}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: 'visible',
                mt: 1.5,
                minWidth: 240,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {user?.full_name || 'Usuário'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Meu Perfil</ListItemText>
            </MenuItem>
            {canAccessAdmin && (
              <MenuItem onClick={() => { handleDrawerToggle(); handleMenuClose(); }}>
                <ListItemIcon>
                  <AdminIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Painel Administrativo</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sair</ListItemText>
            </MenuItem>
          </Menu>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 }, backgroundColor: '#f4f6f8' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
